import { useState, useEffect } from 'react';
import { Fetch } from '../helpers/fetchWrapper';
import { useCancellablePromise } from '../helpers/promiseHandler';

const STATUS = {
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
};

const campaignService = {
  _url: process.env.REACT_APP_CAMPAIGN_API_URL,

  GetAllCampaigns(searchQuery, fetch) {
    const [campaigns, setCampaigns] = useState({
      campaigns: [],
      totalItems: 0,
    });
    const { cancellablePromise } = useCancellablePromise();
    const [status, setStatus] = useState(STATUS.LOADING);
    useEffect(() => {
      setStatus(STATUS.LOADING);
      cancellablePromise(this.getAllCampaigns(searchQuery))
        .then(res => {
          setCampaigns(() => res);
          setStatus(STATUS.SUCCESS);
        })
        .catch(() => setStatus(STATUS.ERROR));
    }, [JSON.stringify(searchQuery), fetch]);

    return {
      campaigns_loading: status === STATUS.LOADING,
      campaigns_error: status === STATUS.ERROR,
      campaigns_data: campaigns,
    };
  },

  GetAllEmails(searchQuery, fetch) {
    const [emails, setEmails] = useState({
      emails: [],
      totalItems: 0,
    });
    const { cancellablePromise } = useCancellablePromise();
    const [status, setStatus] = useState(STATUS.LOADING);
    useEffect(() => {
      setStatus(STATUS.LOADING);
      cancellablePromise(this.getAllEmails(searchQuery))
        .then(res => {
          setEmails(() => res);
          setStatus(STATUS.SUCCESS);
        })
        .catch(() => setStatus(STATUS.ERROR));
    }, [JSON.stringify(searchQuery), fetch]);

    return {
      emails_loading: status === STATUS.LOADING,
      emails_error: status === STATUS.ERROR,
      emails_data: emails,
    };
  },

  GetCampaignGraphAnalytics(searchQuery, fetch) {
    const [kpi, setKpi] = useState({
      kpi: [],
      totalItems: 0,
    });
    const { cancellablePromise } = useCancellablePromise();
    const [status, setStatus] = useState(STATUS.LOADING);
    useEffect(() => {
      if (!searchQuery) {
        setStatus(STATUS.SUCCESS);
        return;
      }
      setStatus(STATUS.LOADING);
      cancellablePromise(this.getCampaignGraphAnalytics(searchQuery))
        .then(res => {
          setKpi(() => res);
          setStatus(STATUS.SUCCESS);
        })
        .catch(() => setStatus(STATUS.ERROR));
    }, [JSON.stringify(searchQuery), fetch]);

    return {
      campaign_graph_loading: status === STATUS.LOADING,
      campaign_graph_error: status === STATUS.ERROR,
      campaign_graph_data: kpi,
    };
  },

  GetCampaignAnalytics(searchQuery, fetch) {
    const [kpi, setKpi] = useState({
      kpi: [],
      totalItems: 0,
    });
    const { cancellablePromise } = useCancellablePromise();
    const [status, setStatus] = useState(STATUS.LOADING);
    useEffect(() => {
      if (!searchQuery) {
        setStatus(STATUS.SUCCESS);
        return;
      }
      setStatus(STATUS.LOADING);
      cancellablePromise(this.getCampaignAnalytics(searchQuery))
        .then(res => {
          setKpi(() => res);
          setStatus(STATUS.SUCCESS);
        })
        .catch(() => setStatus(STATUS.ERROR));
    }, [JSON.stringify(searchQuery), fetch]);

    return {
      campaign_analytics_loading: status === STATUS.LOADING,
      campaign_analytics_error: status === STATUS.ERROR,
      campaign_analytics_data: kpi,
    };
  },

  GetCampaignLeads(searchQuery, fetch) {
    const [leadsData, setLeadsData] = useState({
      leadsData: [],
      totalItems: 0,
    });
    const { cancellablePromise } = useCancellablePromise();
    const [status, setStatus] = useState(STATUS.LOADING);
    useEffect(() => {
      if (!searchQuery || !searchQuery.campaignId) {
        setStatus(STATUS.SUCCESS);
        return;
      }
      setStatus(STATUS.LOADING);
      cancellablePromise(this.getCampaignsLeads(searchQuery))
        .then(res => {
          setLeadsData(() => res);
          setStatus(STATUS.SUCCESS);
        })
        .catch(() => setStatus(STATUS.ERROR));
    }, [JSON.stringify(searchQuery), fetch]);

    return {
      campaign_leads_loading: status === STATUS.LOADING,
      campaign_leads_error: status === STATUS.ERROR,
      campaign_leads_data: leadsData,
    };
  },


  GetAllPendingApprovals(searchQuery, fetch) {
    const [pendingApprovals, setPendingApprovals] = useState({
      pendingApprovals: [],
      totalItems: 0,
    });
    const { cancellablePromise } = useCancellablePromise();
    const [status, setStatus] = useState(STATUS.LOADING);
    useEffect(() => {
      if (!searchQuery) {
        setStatus(STATUS.SUCCESS);
        return;
      }
      setStatus(STATUS.LOADING);
      cancellablePromise(this.getALlPendingApprovals(searchQuery))
        .then(res => {
          setPendingApprovals(() => res);
          setStatus(STATUS.SUCCESS);
        })
        .catch(() => setStatus(STATUS.ERROR));
    }, [JSON.stringify(searchQuery), fetch]);

    return {
      pending_approvals_loading: status === STATUS.LOADING,
      pending_approvals_error: status === STATUS.ERROR,
      pending_approvals_data: pendingApprovals,
    };
  },

  async getAllCampaigns({
    page = 1,
    itemsPerPage = 10,
    searchText = '',
    getAll = false,
    startDate = '',
    endDate = '',
    status = 'all',
    orderBy = 'newest_first',
  }) {
    let res = await Fetch.get(
      `${this._url}/workspace-campaigns/?page=${page}&itemsPerPage=${itemsPerPage}&searchText=${searchText}&getAll=${getAll}&startDate=${startDate}&endDate=${endDate}&status=${status}&orderBy=${orderBy}`,
    );
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something Went Wrong');
  },

  async getCampaignsLeads({ page = 1, itemsPerPage = 10, searchText = '', getAll = false, campaignId }) {
    let res = await Fetch.get(
      `${this._url}/get-campaign-leads/${campaignId}?page=${page}&itemsPerPage=${itemsPerPage}&searchText=${searchText}&getAll=${getAll}`,
    );
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something Went Wrong');
  },

  async addCampaignEnrichment(payload) {
    let res = await Fetch.post(`${this._url}/add-campaign-enrichment`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async addLeadsToCompaign(payload) {
    let res = await Fetch.post(`${this._url}/add-leads-to-campaign`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async scheduleSettingCampaign(payload) {
    let res = await Fetch.post(`${this._url}/schedule-setting-campaign`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async addSequenceToCampaign(payload) {
    let res = await Fetch.post(`${this._url}/add-sequence-list-to-campaign`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async getAllEmails({ page = 1, itemsPerPage = 10, searchText = '', getAll = false, startDate = '', endDate = '' }) {
    let res = await Fetch.get(
      `${this._url}/get-all-email-accounts?page=${page}&itemsPerPage=${itemsPerPage}&searchText=${searchText}&getAll=${getAll}&startDate=${startDate}&endDate=${endDate}`,
    );
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something Went Wrong');
  },

  async addEmailAccountsToCampaign(payload) {
    let res = await Fetch.post(`${this._url}/add-email-account-list-to-campaign`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async getCampaignDetail(id) {
    let res = await Fetch.get(`${this._url}/get-campaign-detail/${id}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async getCampaignAnalytics(id) {
    let res = await Fetch.get(`${this._url}/get-campaign-analytics/${id}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something Went Wrong');
  },

  async getCampaignGraphAnalytics(id) {
    let res = await Fetch.get(`${this._url}/get-graph-analytics/${id}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something Went Wrong');
  },

  async getCampaignSequences(id) {
    let res = await Fetch.get(`${this._url}/get-campaign-sequences/${id}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async getCampaignScheduleSettings(id) {
    let res = await Fetch.get(`${this._url}/get-schedule-settings/${id}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async getCampaignEmailAccounts(id) {
    let res = await Fetch.get(`${this._url}/get-email-accounts/${id}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async getAllSequence(id) {
    let res = await Fetch.get(`${this._url}/get-all-sequences/${id}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async getEnrichLeadsColumn(payload) {
    let res = await Fetch.post(`${this._url}/get-enrich-leads-columns`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async addHoudinyLeadsToCampaign(payload) {
    let res = await Fetch.post(`${this._url}/add-houdiny-leads-to-campaign`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async getCompanyDetails(website) {
    let res = await Fetch.post(`${this._url}/get-company-info`, { website });
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async resumeCampaign(id) {
    let res = await Fetch.patch(`${this._url}/resume-paused-campaign/${id}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async deleteCampaign(id) {
    let res = await Fetch.delete(`${this._url}/delete-campaign/${id}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async deleteCampaignSequence(payload) {
    let res = await Fetch.delete(`${this._url}/delete-campaign-sequence`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async renameCampaign(payload) {
    let res = await Fetch.patch(`${this._url}/rename-campaign`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async pauseCampaign(id) {
    let res = await Fetch.patch(`${this._url}/pause-campaign/${id}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async addMoreLeadsToCampaign(id, payload) {
    let res = await Fetch.post(`${this._url}/add-more-leads-to-campaign/${id}`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async unSubscribeCampaign(token) {
    let res = await Fetch.get(`${this._url}/campaign/${token}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async downloadCampaignAnalyticsCsv(id) {
    let res = await Fetch.get(`${this._url}/get-analytics-report/${id}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async connectEmail(payload) {
    let res = await Fetch.post(`${this._url}/smtp/accounts`, payload);

    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async bulkConnectEmails(payload) {
    let res = await Fetch.post(`${this._url}/smtp/bulk-accounts`, payload);

    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async addNewLeadsToCampaign(payload) {
    let res = await Fetch.post(`${this._url}/add-new-houdiny-leads-to-campaign`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async addLeadsFromSublistToCampaign(payload) {
    let res = await Fetch.post(`${this._url}/add-list-leads-to-campaign`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async addUserLeadToCampaign(payload) {
    let res = await Fetch.post(`${this._url}/add-user-lead-to-campaign`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async previewSublistLeadsToCampaign(payload) {
    let res = await Fetch.post(`${this._url}/preview-sublist-leads`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async getUserLeadsPreview(payload) {
    let res = await Fetch.post(`${this._url}/preview-user-leads`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },



  async addLeadsFromJobToCampaign(payload) {
    let res = await Fetch.post(`${this._url}/add-job-leads-to-campaign`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async connectWithAuth() {
    let res = await Fetch.get(`${this._url}/smtp/google`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async connectWithOutlookAuth() {
    let res = await Fetch.get(`${this._url}/smtp/outlook`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async getWorkspaceCampaignTotals() {
    let res = await Fetch.get(`${this._url}/workspace-campaigns-totals`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async WarmupCampaign(id, payload) {
    let res = await Fetch.patch(`${this._url}/smtp/warm-up-email/${id}`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async AiCampaign(payload) {
    let res = await Fetch.post(`${this._url}/ai-campaign`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async GetAiCampaign(id) {
    let res = await Fetch.get(`${this._url}/ai-campaign/${id}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async getALlPendingApprovals({ page = 1, itemsPerPage = 10, campaignId = '' }) {
    let res = await Fetch.get(`${this._url}/ai-campaign/pending-approvals?page=${page}&itemsPerPage=${itemsPerPage}&campaignId=${campaignId}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },


  async deletePendingApproval(id) {
    let res = await Fetch.delete(`${this._url}/ai-campaign/lead-status/${id}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async updatePendingApproval(id, payload) {
    let res = await Fetch.patch(`${this._url}/ai-campaign/update-user-lead/${id}`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async approvePendingApproval(id, payload) {
    let res = await Fetch.patch(`${this._url}/ai-campaign/lead-status/${id}/approve`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async approveAllPendingApprovals(payload) {
    let res = await Fetch.patch(`${this._url}/ai-campaign/lead-status/approve-all`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
};

export default campaignService;
